/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { domainsSetRootMcpEndpoint } from "../funcs/domainsSetRootMcpEndpoint.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { CustomDomain } from "../models/components/customdomain.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetRootMcpEndpointRequest,
  SetRootMcpEndpointSecurity,
} from "../models/operations/setrootmcpendpoint.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetRootMcpEndpointMutationVariables = {
  request: SetRootMcpEndpointRequest;
  security?: SetRootMcpEndpointSecurity | undefined;
  options?: RequestOptions;
};

export type SetRootMcpEndpointMutationData = CustomDomain;

export type SetRootMcpEndpointMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setRootMcpEndpoint domains
 *
 * @remarks
 * Set or clear the MCP endpoint mapped to a custom domain's root. Pass mcp_endpoint_id for an endpoint already attached to the domain, or mcp_server_id to attach a server (creating its domain endpoint if needed) and map it in one call — usable while the domain is still pending verification, so a migration can be staged before DNS cuts over.
 */
export function useSetRootMcpEndpointMutation(
  options?: MutationHookOptions<
    SetRootMcpEndpointMutationData,
    SetRootMcpEndpointMutationError,
    SetRootMcpEndpointMutationVariables
  >,
): UseMutationResult<
  SetRootMcpEndpointMutationData,
  SetRootMcpEndpointMutationError,
  SetRootMcpEndpointMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetRootMcpEndpointMutation(client, options),
    ...options,
  });
}

export function mutationKeySetRootMcpEndpoint(): MutationKey {
  return ["@gram/client", "domains", "setRootMcpEndpoint"];
}

export function buildSetRootMcpEndpointMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetRootMcpEndpointMutationVariables,
  ) => Promise<SetRootMcpEndpointMutationData>;
} {
  return {
    mutationKey: mutationKeySetRootMcpEndpoint(),
    mutationFn: function setRootMcpEndpointMutationFn({
      request,
      security,
      options,
    }): Promise<SetRootMcpEndpointMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(domainsSetRootMcpEndpoint(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
